'use client';

import { useSyncExternalStore } from 'react';

interface ChartColors {
  gridColor: string;
  tooltipBg: string;
  tooltipBorder: string;
  axisColor: string;
  textColor: string;
}

const SSR_FALLBACK: ChartColors = {
  gridColor: '#334155',
  tooltipBg: '#1e293b',
  tooltipBorder: '#475569',
  axisColor: '#64748b',
  textColor: '#fafafa',
};

function readColors(): ChartColors {
  const style = getComputedStyle(document.documentElement);
  return {
    gridColor: style.getPropertyValue('--color-chart-grid').trim() || '#334155',
    tooltipBg: style.getPropertyValue('--color-chart-tooltip-bg').trim() || '#1e293b',
    tooltipBorder: style.getPropertyValue('--color-chart-tooltip-border').trim() || '#475569',
    axisColor: style.getPropertyValue('--color-chart-axis').trim() || '#64748b',
    textColor: style.getPropertyValue('--color-text').trim() || '#fafafa',
  };
}

let cachedColors: ChartColors | null = null;

function getSnapshot(): ChartColors {
  if (!cachedColors) {
    cachedColors = readColors();
  }
  return cachedColors;
}

function getServerSnapshot(): ChartColors {
  return SSR_FALLBACK;
}

function subscribe(onStoreChange: () => void): () => void {
  // Invalidate cache and notify on theme attribute changes
  const observer = new MutationObserver(() => {
    requestAnimationFrame(() => {
      cachedColors = null;
      onStoreChange();
    });
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });

  return () => observer.disconnect();
}

export function useChartColors(): ChartColors {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
